import * as React from 'react';
import { cx } from 'linaria';
import { Typography, RegularButton } from 'fronton-react';
import { jc } from 'utils';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { WishlistButton } from 'features/product-info/components/wishlist-button';
import { PdpComparison } from 'features/product-info/components/pdp-comparison';
import {
  descriptionStyle,
  flexVerticalCenter,
  image,
  leftSide,
  mediumPlusScreenProduct,
  mediumPlusScreenProductButton,
  mediumPlusScreenProductItem,
  mediumPlusScreenProductOutOfStock,
  mobileProduct,
  tabletProduct,
  textWrapper,
} from './product.styles';
import { ProductProps } from '../types/product-props';
import { OutOfStockMessage } from './out-of-stock-message';
import { useTranslation } from 'react-i18next';

export const ProductImage: React.FC<{ url: string; description: string; onClick: () => void }> = ({
  description,
  onClick,
  url,
}) => {
  const { t } = useTranslation();
  return (
    <ImagePlaceholder
      onClick={onClick}
      alt={description}
      fallbackAlt={t('ui.image.alt')}
      src={url}
      loading="lazy"
      className={image}
      fallbackWidth={40}
      fallbackHeight={40}
    />
  );
};

export const ProductDescription: React.FC<{ description: string }> = ({ description }) => {
  return (
    <Typography variant="s" size="body_short" className={descriptionStyle}>
      {description}
    </Typography>
  );
};

export const MobileProduct: React.FC<ProductProps> = ({ availabilityState, onSectionActivated, slots }) => {
  return (
    <>
      <div data-qa="mobile-product" className={mobileProduct}>
        <div className={leftSide}>
          {slots.image}
          <div className={textWrapper}>
            {slots.description}
            {availabilityState.availability === 'available' ? (
              slots.price
            ) : (
              <OutOfStockMessage onSectionActivated={onSectionActivated} />
            )}
          </div>
        </div>
        {availabilityState.availability === 'available' ? slots.basket : null}
      </div>
    </>
  );
};

export const TabletProduct: React.FC<ProductProps> = ({ availabilityState, onSectionActivated, slots }) => (
  <>
    <div data-qa="tablet-product" className={tabletProduct}>
      {availabilityState.availability === 'available' ? (
        <>
          <div className={leftSide}>
            {slots.image}
            <div className={textWrapper}>
              <div>{slots.description}</div>
              {slots.price}
            </div>
          </div>
          {slots.basket}
        </>
      ) : (
        <>
          <div className={leftSide}>
            {slots.image}
            <div className={jc(textWrapper, flexVerticalCenter)}>{slots.description}</div>
          </div>
          <OutOfStockMessage onSectionActivated={onSectionActivated} />
        </>
      )}
    </div>
  </>
);

export const MediumPlusScreenProduct: React.FC<ProductProps> = ({
  availabilityState,
  catalogue4LevelLink = '',
  onSectionActivated,
  quantity = 0,
  slots,
}) => {
  const { t } = useTranslation();

  const inBasket = quantity > 0;
  const outOfStock = availabilityState.availability === 'out-of-stock';
  const similar = t('product-info.availability.show-similar');

  return (
    <div className={mediumPlusScreenProduct}>
      <div
        className={cx(mediumPlusScreenProductItem, outOfStock ? mediumPlusScreenProductOutOfStock : null)}
      >
        {slots.price}
        {outOfStock && <OutOfStockMessage onSectionActivated={onSectionActivated} />}
      </div>
      {outOfStock && catalogue4LevelLink && (
        <RegularButton
          className={mediumPlusScreenProductItem}
          href={catalogue4LevelLink}
          variant="secondary"
          size="m"
        >
          {similar}
        </RegularButton>
      )}
      {!outOfStock && <div className={mediumPlusScreenProductItem}>{slots.basket}</div>}
      {!inBasket && (
        <>
          <RegularButton
            className={mediumPlusScreenProductButton}
            size="m"
            variant="secondary"
            aria-label=""
          >
            <WishlistButton productDataIsLoading={false} size="s" iconOnly />
          </RegularButton>
          <RegularButton
            className={mediumPlusScreenProductButton}
            size="m"
            variant="secondary"
            aria-label=""
          >
            <PdpComparison iconOnly />
          </RegularButton>
        </>
      )}
    </div>
  );
};
